import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { donationService } from '../../services';

const PaymentCallback = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('processing'); // processing, success, error
    const [message, setMessage] = useState('Verifying your payment...');
    const verifyStarted = useRef(false);

    const verifyPayment = useCallback(async (pidx) => {
        if (verifyStarted.current) return;
        verifyStarted.current = true;

        try {
            setStatus('processing');
            const response = await donationService.verifyPayment({ pidx });

            if (response.data?.status === 'success' || (response.status === true && (response.statusCode === 200 || response.statusCode === 201))) {
                setStatus('success');
                setMessage('Payment successful! Thank you for your donation.');
                setTimeout(() => navigate('/donations'), 3000);
            } else {
                setStatus('error');
                setMessage(response.message || 'Payment verification failed.');
            }
        } catch (error) {
            console.error('Payment verification error:', error);
            setStatus('error');
            setMessage(error.response?.data?.message || 'An error occurred during payment verification.');
            // Redirect to donations page after 3 seconds on error
            setTimeout(() => navigate('/donations'), 3000);
        }
    }, [navigate]);

    const verifyEsewaPayment = useCallback(async (base64Data, urlStatus) => {
        if (verifyStarted.current) return;
        verifyStarted.current = true;

        try {
            setStatus('processing');
            setMessage('Verifying your eSewa payment...');
            
            const response = await donationService.verifyPayment({ 
                data: base64Data,
                status: urlStatus,
                paymentMethod: 'ESEWA'
            });

            if (response.data?.status === 'success' || (response.status === true && (response.statusCode === 200 || response.statusCode === 201))) {
                setStatus('success');
                setMessage('Payment successful! Thank you for your donation.');
                setTimeout(() => navigate('/donations'), 3000);
            } else {
                setStatus('error');
                setMessage(response.message || 'Payment verification failed.');
                setTimeout(() => navigate('/donations'), 3000);
            }
        } catch (error) {
            console.error('eSewa payment verification error:', error);
            setStatus('error');
            setMessage(error.response?.data?.message || 'An error occurred during payment verification.');
            setTimeout(() => navigate('/donations'), 3000);
        }
    }, [navigate]);

    const verifyConnectIpsPayment = useCallback(async (txnId, urlStatus) => {
        if (verifyStarted.current) return;
        verifyStarted.current = true;

        try {
            setStatus('processing');
            setMessage('Verifying your ConnectIPS payment...');
            
            const response = await donationService.verifyPayment({ 
                txnId: txnId,
                status: urlStatus,
                paymentMethod: 'CONNECTIPS'
            });

            if (response.data?.status === 'success' || (response.status === true && (response.statusCode === 200 || response.statusCode === 201))) {
                setStatus('success');
                setMessage('Payment successful! Thank you for your donation.');
                setTimeout(() => navigate('/donations'), 3000);
            } else {
                setStatus('error');
                setMessage(response.message || 'Payment verification failed.');
                setTimeout(() => navigate('/donations'), 3000);
            }
        } catch (error) {
            console.error('ConnectIPS payment verification error:', error);
            setStatus('error');
            setMessage(error.response?.data?.message || 'An error occurred during payment verification.');
            setTimeout(() => navigate('/donations'), 3000);
        }
    }, [navigate]);

    useEffect(() => {
        // Parse URL manually to handle eSewa's double ? issue (?status=success?data=...)
        const search = window.location.search;
        let esewaData = null;
        let urlStatus = null;
        
        // Check if this is eSewa response (has data parameter)
        if (search.includes('data=')) {
            // Handle both ?status=success&data= and ?status=success?data= (double ?)
            const dataMatch = search.match(/[?&]data=([^&]+)/);
            if (dataMatch) {
                esewaData = decodeURIComponent(dataMatch[1]);
            }
            
            // Get status (might be before or after data)
            const statusMatch = search.match(/[?&]status=([^&?]+)/);
            if (statusMatch) {
                urlStatus = statusMatch[1];
            } else if (search.includes('status=success')) {
                urlStatus = 'success';
            } else if (search.includes('status=failed')) {
                urlStatus = 'failed';
            }
        }
        
        // Handle eSewa response
        if (esewaData) {
            if (urlStatus === 'success' || urlStatus === 'failed') {
                verifyEsewaPayment(esewaData, urlStatus);
            } else {
                setStatus('error');
                setMessage('Invalid payment status received from eSewa.');
                setTimeout(() => navigate('/donations'), 3000);
            }
            return;
        }

        // Handle Khalti response (has pidx)
        const pidx = searchParams.get('pidx');
        const khaltiStatus = searchParams.get('status');

        if (pidx) {
            if (khaltiStatus === 'Completed' || !khaltiStatus) {
                verifyPayment(pidx);
            } else if (khaltiStatus === 'User canceled') {
                setStatus('error');
                setMessage('Payment was cancelled by the user.');
                setTimeout(() => navigate('/donations'), 3000);
            } else {
                setStatus('error');
                setMessage(`Payment failed with status: ${khaltiStatus}`);
                setTimeout(() => navigate('/donations'), 3000);
            }
            return;
        }

        // Handle ConnectIPS response (has status and txnId/TXNID, but no pidx or data)
        const connectIpsStatus = searchParams.get('status');
        const txnId = searchParams.get('txnId') || searchParams.get('TXNID') || searchParams.get('txnid');
        
        if (connectIpsStatus && txnId) {
            verifyConnectIpsPayment(txnId, connectIpsStatus);
            return;
        }

        // If we reach here, we don't recognize the payment callback format
        setStatus('error');
        setMessage('Invalid payment information received.');
        setTimeout(() => navigate('/donations'), 3000);
    }, [searchParams, navigate, verifyPayment, verifyEsewaPayment, verifyConnectIpsPayment]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
            <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
                {status === 'processing' && (
                    <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary-600 mb-6"></div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment</h2>
                        <p className="text-gray-600">{message}</p>
                    </div>
                )}

                {status === 'success' && (
                    <div className="flex flex-col items-center">
                        <div className="bg-green-100 rounded-full p-4 mb-6">
                            <svg className="h-12 w-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
                        <p className="text-gray-600 mb-6">{message}</p>
                        <p className="text-sm text-gray-500">Redirecting to your donations...</p>
                    </div>
                )}

                {status === 'error' && (
                    <div className="flex flex-col items-center">
                        <div className="bg-red-100 rounded-full p-4 mb-6">
                            <svg className="h-12 w-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Failed</h2>
                        <p className="text-gray-600 mb-6">{message}</p>
                        <button
                            onClick={() => navigate('/donations')}
                            className="bg-primary-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-primary-700 transition"
                        >
                            Back to Donations
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentCallback;
